import { initializeApp } from 'firebase/app';
import { 
  getFirestore, 
  doc, 
  getDoc, 
  collection, 
  setDoc, 
  updateDoc,
  increment,
  arrayUnion,
  writeBatch
} from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { Question } from '@/types/questions';
import { getAuth, GoogleAuthProvider } from 'firebase/auth';

// Remove the environment variable check and use the config directly
const firebaseConfig = {
  apiKey: "AIzaSyCR9_J--ElJBZJ1n6E_wlcjLCZyenmf0MI",
  authDomain: "utme-past-questions-2010-2018.firebaseapp.com",
  projectId: "utme-past-questions-2010-2018",
  storageBucket: "utme-past-questions-2010-2018.firebasestorage.app",
  messagingSenderId: "11586823034",
  appId: "1:11586823034:web:d625a4162bd0f19457ecc3"
};

// Initialize Firebase
export const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const db = getFirestore(app);
export const storage = getStorage(app);

// Configure Google provider
export const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({
  prompt: 'select_account'
});

interface QuestionResponse {
  questions: Question[];
  totalCount: number;
}

export interface UserData {
  questionAttempts: number;
  isPremium: boolean;
  lastAccessedYear?: string;
  paymentHistory?: PaymentTransaction[];
}

export interface PaymentTransaction {
  reference: string;
  amount: number;
  timestamp: Date;
  status: 'success' | 'failed';
  userEmail: string;
}

export const getQuestionsBySubjectAndYear = async (subject: string, year: string): Promise<QuestionResponse> => {
  try {
    console.log('Fetching questions for:', subject, year);
    
    // Get reference to the subject collection and year document
    const subjectRef = collection(db, subject.toLowerCase());
    const yearDocRef = doc(subjectRef, year);
    
    // Get the document
    const yearDocSnap = await getDoc(yearDocRef);
    
    if (!yearDocSnap.exists()) {
      console.log(`No document found for ${subject} ${year}`);
      return { questions: [], totalCount: 0 };
    }

    const data = yearDocSnap.data();
    const questions = data.questions || [];
    
    console.log(`Found ${questions.length} questions for ${subject} ${year}`);
    
    return { 
      questions,
      totalCount: questions.length 
    };
  } catch (error) {
    console.error('Error fetching questions:', error);
    throw error;
  }
};

export const uploadQuestions = async (
  subject: string,
  year: string,
  questions: Question[]
) => {
  try {
    // Fix: Use the correct collection/document structure
    const subjectRef = collection(db, subject.toLowerCase());
    const yearDocRef = doc(subjectRef, year);
    
    await setDoc(yearDocRef, {
      subject,
      year,
      questions,
      totalQuestions: questions.length
    });
    console.log('Questions uploaded successfully');
  } catch (error) {
    console.error('Error uploading questions:', error);
    throw error;
  }
};

export const updateQuestionImage = async (
  subject: string,
  year: string,
  questionId: string,
  imageUrl: string
): Promise<void> => {
  try {
    console.log('Updating image for:', { subject, year, questionId, imageUrl });

    const subjectRef = collection(db, subject.toLowerCase());
    const yearDocRef = doc(subjectRef, year);
    const yearDocSnap = await getDoc(yearDocRef);

    if (!yearDocSnap.exists()) {
      throw new Error('Document does not exist');
    }

    const data = yearDocSnap.data();
    console.log('Current document data:', data);

    // Get the questions array
    const questions = data.questions || [];
    
    // Find the question by its index (since your data uses numeric indices)
    const questionIndex = parseInt(questionId) - 1; // Convert from 1-based to 0-based index
    
    if (questionIndex < 0 || questionIndex >= questions.length) {
      throw new Error('Question index out of range');
    }

    // Create a new questions array with the updated question
    const updatedQuestions = [...questions];
    updatedQuestions[questionIndex] = {
      ...updatedQuestions[questionIndex],
      imageUrl: imageUrl,
      // Preserve existing fields
      question: updatedQuestions[questionIndex].question,
      answer: updatedQuestions[questionIndex].answer,
      options: updatedQuestions[questionIndex].options
    };

    console.log('Updating question at index:', questionIndex);
    console.log('Updated question:', updatedQuestions[questionIndex]);

    // Update the document with the new questions array
    await updateDoc(yearDocRef, {
      questions: updatedQuestions
    });

    console.log('Document successfully updated');
  } catch (error) {
    console.error('Error in updateQuestionImage:', error);
    throw error;
  }
};

export const parseAndUploadQuestions = async (
  subject: string,
  year: string,
  fileContent: string
) => {
  try {
    const questions = JSON.parse(fileContent);
    if (!Array.isArray(questions)) {
      throw new Error('Invalid questions format');
    }

    const formattedQuestions = questions.map((q: any, index: number) => ({
      id: String(index + 1),
      text: q.question || '',
      question: q.question || '',
      options: q.options || [],
      answer: q.answer || '',
      imageUrl: q.imageUrl || '',
      subject: subject,
      year: year
    }));

    await uploadQuestions(subject, year, formattedQuestions);
    return true;
  } catch (error) {
    if (error instanceof Error) {
      console.error('Error parsing and uploading questions:', error.message);
    } else {
      console.error('Unknown error parsing and uploading questions');
    }
    return false;
  }
};

export const getUserData = async (userId: string): Promise<UserData | null> => {
  try {
    const userRef = doc(db, 'users', userId);
    const userSnap = await getDoc(userRef);
    
    if (!userSnap.exists()) {
      console.log('No user data found, initializing...');
      await initializeUserData(userId);
      return {
        questionAttempts: 0,
        isPremium: false,
        lastAccessedYear: '2015'
      };
    }

    const userData = userSnap.data() as UserData;
    console.log('getUserData result:', userData);
    return userData;
  } catch (error) {
    console.error('Error getting user data:', error);
    return null;
  }
};

export const incrementQuestionAttempt = async (userId: string) => {
  const userRef = doc(db, 'users', userId);
  await setDoc(userRef, {
    questionAttempts: increment(1)
  }, { merge: true });
};

export const setUserPremium = async (userId: string, transaction: PaymentTransaction) => {
  try {
    console.log('Firebase: Starting premium update for user:', userId);
    const userRef = doc(db, 'users', userId);

    // Create the payment history entry
    const paymentEntry = {
      reference: transaction.reference,
      amount: transaction.amount,
      timestamp: transaction.timestamp.toISOString(),
      status: transaction.status,
      userEmail: transaction.userEmail
    };

    // Create a complete user document
    const userData = {
      isPremium: true, // Explicitly set premium status
      questionAttempts: 0, // Reset attempts
      lastAccessedYear: '2015',
      updatedAt: new Date().toISOString(),
      email: transaction.userEmail,
      paymentHistory: arrayUnion(paymentEntry)
    };

    console.log('Firebase: Writing user data:', userData);

    // Use set WITHOUT merge to ensure we overwrite the document
    await setDoc(userRef, userData);

    // Verify the update immediately
    const updatedDoc = await getDoc(userRef);
    const updatedData = updatedDoc.data();
    console.log('Firebase: Verification read:', updatedData);

    if (!updatedData?.isPremium) {
      throw new Error('Premium status not set in Firebase');
    }

    return updatedData as UserData;
  } catch (error) {
    console.error('Firebase: Error in setUserPremium:', error);
    throw error;
  }
};

export const initializeUserData = async (userId: string) => {
  const userRef = doc(db, 'users', userId);
  await setDoc(userRef, {
    questionAttempts: 0,
    isPremium: false,
    lastAccessedYear: '2015'
  }, { merge: true });
};

// Add this interface to define the question structure
interface PhysicsQuestion {
  id: number;
  question: string;
  options: string[];
  answer: string;
  year: number;
  subject: string;
}

// Update the function to handle different years
export async function addPhysicsQuestions(questions: PhysicsQuestion[]) {
  const batch = writeBatch(db);
  
  // Get the year from the first question
  const year = questions[0].year.toString();
  
  // Reference to the existing physics collection and year document
  const physicsRef = doc(db, 'physics', year);
  
  // Set the document with the new questions array, maintaining the existing structure
  batch.set(physicsRef, {
    questions: questions.map(q => ({
      id: q.id.toString(), // Convert to string to match existing format
      question: q.question,
      options: q.options,
      answer: q.answer
    })),
    totalQuestions: questions.length
  }, { merge: false }); // Using merge: false to completely replace the document

  try {
    await batch.commit();
    console.log(`${year} Physics questions replaced successfully`);
  } catch (error) {
    console.error('Error replacing questions:', error);
    throw error;
  }
}